/* eslint-disable react/prop-types */
import cn from "classnames";
import { isCursorLearnMoreOpenAtom } from "frontend/app-header/left-header/atoms/left-header-atoms";
import Modal from "frontend/modal/modal";
import { Box } from "frontend/ui-components/design-system/box/box";
import TabBar from "frontend/ui-components/design-system/tab-bar/tab-bar";
import { CloseIcon } from "frontend/ui-components/svg-shapes";
import { useAtom } from "jotai";
import { cloneElement, useCallback, useEffect, useState } from "react";
import { NavMode, navModeAtom } from "state-atoms/nav-atoms";
import styles from "./cursor-view.module.css";

export const CursorView = () => {
  const [navMode, setNavMode] = useAtom(navModeAtom);
  const [navModeTab, setNavModeTab] = useState<NavMode>(navMode);
  const [isCursorLearnMoreOpen, setIsCursorLearnMoreOpen] = useAtom(isCursorLearnMoreOpenAtom);

  const handleCloseModal = useCallback(() => {
    setIsCursorLearnMoreOpen(false);
  }, [setIsCursorLearnMoreOpen]);

  useEffect(() => {
    setNavModeTab(navMode);
  }, [navMode]);

  if (!isCursorLearnMoreOpen) return null;

  return (
    <Modal dimBackground onDismissOnEscape={handleCloseModal} onClickBackground={handleCloseModal} theme="cim-dark">
      <Box variant="modal" className={cn(styles.cursorContent)}>
        <div className={styles.close} onClick={handleCloseModal}>
          <CloseIcon color="#848199" scale={1.1} />
        </div>
        <div className={styles.header}>
          <div className={styles.title}>Navigation mode</div>
          <div className={styles.subtitle}>Choose mouse or trackpad</div>
        </div>
        <TabBar
          options={[
            { id: "mouse", renderer: "Mouse" },
            { id: "trackpad", renderer: "Trackpad" },
            { id: "auto-detect", renderer: "Auto-detect" },
          ]}
          selected={navModeTab}
          onChange={(id) => {
            setNavModeTab(id as "mouse" | "trackpad" | "auto-detect");
          }}
          customStyle={{ height: "40px" }}
        />

        <div className={styles.contentWrapper}>
          {navModeTab === "mouse" && <MouseViewContent />}
          {navModeTab === "trackpad" && <TrackpadViewContent />}
          {navModeTab === "auto-detect" && <AutoDetectViewContent />}
        </div>

        <button
          className={styles.saveButton}
          onClick={() => {
            setNavMode(navModeTab);
            handleCloseModal();
          }}
        >
          Save
        </button>
      </Box>
    </Modal>
  );
};

const MOUSE_VIEW_ITEMS = [
  {
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="60" fill="none" viewBox="0 0 48 60">
        <rect width="40.006" height="52.931" x="1.385" y="5.539" stroke="#1E1E1E" strokeWidth="2" rx="15.156"></rect>
        <path stroke="#1E1E1E" strokeWidth="2" d="M.385 26.426h39.672"></path>
        <path stroke="#1E1E1E" strokeLinecap="round" strokeWidth="2" d="M20.771 12.002v9.444"></path>
        <circle cx="36.693" cy="10.892" r="10.892" fill="#1973FF" opacity="0.5"></circle>
      </svg>
    ),
    title: "Right click and drag to pan",
  },
  {
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="42" height="56" fill="none" viewBox="0 0 42 56">
        <rect width="40.006" height="52.931" x="1" y="1.539" stroke="#1E1E1E" strokeWidth="2" rx="15.156"></rect>
        <path stroke="#1E1E1E" strokeWidth="2" d="M0 22.426h39.672"></path>
        <path stroke="#85B2F8" strokeLinecap="round" strokeWidth="2" d="M20.387 8.002v9.444"></path>
      </svg>
    ),
    title: "Scroll with mousewheel to zoom",
  },
];

const TRACKPAD_VIEW_ITEMS = [
  {
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="55" height="52" fill="none" viewBox="0 0 55 52">
        <rect
          width="40.006"
          height="52.931"
          x="53.932"
          y="1.246"
          stroke="#1E1E1E"
          strokeWidth="2"
          rx="9.892"
          transform="rotate(90 53.932 1.246)"
        ></rect>
        <path fill="#fff" d="m27.927 44-3.925-5.778c9.962-.919 16.038-1.303 26-2.222v5.647l-1.417 1.882z"></path>
        <path
          stroke="#1E1E1E"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M29.457 34.554V19.766a2.61 2.61 0 0 1 5.22 0v13.048"
        ></path>
        <path
          stroke="#1E1E1E"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M34.676 23.488v-3.48a2.61 2.61 0 1 1 5.219 0v12.806"
        ></path>
        <path
          stroke="#1E1E1E"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M39.897 30.204a2.61 2.61 0 1 1 5.219 0v2.61"
        ></path>
        <path
          stroke="#1E1E1E"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M45.116 31.945a2.61 2.61 0 1 1 5.219 0v7.828a10.44 10.44 0 0 1-10.439 10.439h-3.48.363a10.44 10.44 0 0 1-8.72-4.697l-.34-.522q-.815-1.25-5.718-9.966a2.61 2.61 0 0 1 .933-3.517 3.25 3.25 0 0 1 3.966.487l2.558 2.557"
        ></path>
        <circle cx="34.492" cy="15.677" r="10.892" fill="#1973FF" opacity="0.5"></circle>
      </svg>
    ),
    title: "Swipe with two fingers to pan",
  },
  {
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="55" height="52" fill="none" viewBox="0 0 55 52">
        <rect
          width="40.006"
          height="52.931"
          x="53.932"
          y="1.246"
          stroke="#1E1E1E"
          strokeWidth="2"
          rx="9.892"
          transform="rotate(90 53.932 1.246)"
        ></rect>
        <path fill="#fff" d="m27.927 44-3.925-5.778c9.962-.919 16.038-1.303 26-2.222v5.647l-1.417 1.882z"></path>
        <path
          stroke="#1E1E1E"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="m29.984 34.683-5.427-13.756a2.61 2.61 0 0 1 4.855-1.916l4.789 12.138M34.492 30.653V17.378a2.61 2.61 0 1 1 5.22 0v12.806M39.897 30.203a2.61 2.61 0 1 1 5.219 0v2.61"
        ></path>
        <path
          stroke="#1E1E1E"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M45.116 31.944a2.61 2.61 0 1 1 5.219 0v7.828a10.44 10.44 0 0 1-10.439 10.439h-3.48.363a10.44 10.44 0 0 1-8.72-4.697l-.34-.522q-.815-1.25-5.718-9.966a2.61 2.61 0 0 1 .933-3.517 3.25 3.25 0 0 1 3.966.487l2.558 2.557"
        ></path>
        <path
          stroke="#85B2F8"
          strokeLinecap="round"
          strokeWidth="1.815"
          d="m16.899 29.788-3.644.28a.454.454 0 0 1-.488-.487l.28-3.644M18.97 24.048l-4.96 4.777M32.861 7.032l3.644-.28a.454.454 0 0 1 .487.487l-.28 3.644M30.79 12.772l4.96-4.778"
        ></path>
      </svg>
    ),
    title: "Pinch with two fingers to zoom",
  },
];

const ContentItem = ({ icon, title }: { icon: React.ReactNode; title: string }) => {
  return (
    <div className={styles.contentItem}>
      {cloneElement(icon as React.ReactElement, {
        className: styles.contentItemIcon,
        style: { width: "100%", height: "55px" },
      })}
      <div className={styles.contentItemTitle}>{title}</div>
    </div>
  );
};

const ContentView = ({ items }: { items: { icon: React.ReactNode; title: string }[] }) => {
  return (
    <div className={styles.contentView}>
      {items.map((item) => (
        <ContentItem key={item.title} {...item} />
      ))}
    </div>
  );
};

const MouseViewContent = () => {
  return <ContentView items={MOUSE_VIEW_ITEMS} />;
};

const TrackpadViewContent = () => {
  return <ContentView items={TRACKPAD_VIEW_ITEMS} />;
};

const AutoDetectViewContent = () => {
  return <ContentView items={[...MOUSE_VIEW_ITEMS, ...TRACKPAD_VIEW_ITEMS]} />;
};
